import { ExclamationCircleOutlined, HistoryOutlined } from '@ant-design/icons';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import LoadingOutlined from '@ant-design/icons/LoadingOutlined';
import LockOutlined from '@ant-design/icons/LockOutlined';
import PlusCircleOutlined from '@ant-design/icons/PlusCircleOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import Tooltip from 'antd/es/tooltip';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import Table, { TablePaginationConfig } from 'antd/lib/table';
import { ColumnFilterItem, ColumnsType, FilterValue, SorterResult } from 'antd/lib/table/interface';
import Typography from 'antd/lib/typography';
import isEmpty from 'lodash/isEmpty';
import React, { useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AbilityContext } from '../../components/ability/can';
import { LoadingIcon } from '../../components/icons/LoadingIcon';
import { AuditTrailModal } from '../../components/modals/AuditTrailModal/AuditTrailModal';
import { PageHeader } from '../../components/PageHeader';
import { ProgramExportButton } from '../../components/program/ProgramExportButton';
import { ProgramFormModal } from '../../components/program/ProgramFormModal';
import { ProgramUtilityAutomations } from '../../components/program/ProgramUtilityAutomations';
import { UploadProgramToProductRelationModal } from '../../components/program/UploadProgramToProductRelationModal';
import { SyncSalesforceButton } from '../../components/salesforce/SyncSalesforceButton/SyncSalesforceButton';
import { SearchInput } from '../../components/searchInput/searchInput';
import { useCompanies } from '../../components/selectors/GlobalCompanySelector/CompanyContext';
import { filterCompaniesByType } from '../../components/selectors/GlobalCompanySelector/companyHelpers';
import { ActionList } from '../../components/table/actionList/actionList';
import { buildUtilityCustomerFilter, getColumnFilteredValue, getColumnSortOrder } from '../../components/table/columnFormatHelpers';
import { getTablePaginationParams } from '../../components/table/getTablePaginationParams';
import { WithEmptyDataTable } from '../../components/table/withEmptyDataTable';
import { useDocumentTitle } from '../../components/useDocumentTitle';
import { UtilityOrIsoTag } from '../../components/UtilityOrIsoTag';
import { JobName } from '../../domain/async-job/interface';
import { AuditEntityType } from '../../domain/audit-trail/interface';
import { useAuth } from '../../domain/auth/useAuth';
import { DEFAULT_PAGINATION, PAGE_ACTION } from '../../domain/commonConst';
import { COMPANY_TYPE } from '../../domain/company/interface';
import { compareWithLocale } from '../../domain/compareWithLocale';
import { IPaginationMeta } from '../../domain/IPagination';
import { IProgram } from '../../domain/program/interface';
import { useProgramDeleteMutation, useProgramListQuery, useProgramOperatorsListQuery, useProgramTypesListQuery } from '../../domain/program/queries';
import { useProgramUtilityAutomationFilterOptions } from '../../domain/program/useProgramUtilityAutomationFilterOptions';
import { exportSites } from '../../domain/site';
import { toUsefulParams } from '../toUsefulParams';
import { usePageLocation } from '../usePageState';
import { numberFormatter } from '../../domain/common/numberFormatter';

export const defaultState = {
    pagination: DEFAULT_PAGINATION,
    sorter: {
        field: 'enrolled_sites_count',
        order: 'descend',
    },
};

export const Programs = () => {
    const { notification, modal } = App.useApp();
    const location = useLocation();
    const { companies } = useCompanies()!;
    const { setPageQuery, queryToState } = usePageLocation();

    const { companyId, ...queryState } = queryToState(location.search);

    // need to handle companyId separately to fill default state
    const pageState: any = isEmpty(queryState) ? { companyId, ...defaultState } : { companyId, ...queryState };

    useDocumentTitle('Programs');

    const [program, setProgram] = useState<IProgram | Partial<IProgram> | null>(null);
    const [sitesExportLoading, setSitesExportLoading] = useState<boolean>(false);
    const [pageAction, setPageAction] = useState<PAGE_ACTION | ''>();

    const auth = useAuth()!;
    const ability = useContext(AbilityContext);

    const utilityCustomerCompanies = useMemo(() => filterCompaniesByType(companies, COMPANY_TYPE.UTILITY_CUSTOMER), [companies]);

    const { mutate: deleteProgram, isLoading: isProgramDeleteLoading } = useProgramDeleteMutation();

    const params = toUsefulParams(pageState);

    const listQuery = {
        pagination: params?.pagination ?? DEFAULT_PAGINATION,
        ...(params?.filter && { filter: params?.filter }),
        ...(params?.sorter && { sorter: params?.sorter }),
        ...(params?.search && { search: params?.search }),
        include: 'enrolled-sites-count,products,meter_enrollments,utility_customer,cloud_ven',
        company_id: params?.company_id ?? null,
    };

    const {
        data,
        isError,
        error,
        isFetching,
        refetch: refetchProgramList,
    } = useProgramListQuery(listQuery, {
        keepPreviousData: true,
    });

    const { data: operatorsData } = useProgramOperatorsListQuery();
    const operators = operatorsData || [];

    const { data: typesData } = useProgramTypesListQuery();
    const types = typesData || [];

    const utilityAutomationFilterOptions = useProgramUtilityAutomationFilterOptions(params.company_id);

    const programs = data?.data || [];
    const meta = data?.meta || ({} as IPaginationMeta);

    if (isError) {
        notification.error({ key: 'programs-fetch-error', message: error?.message || 'Cannot fetch programs!' });
    }

    function showNewProgramModal() {
        setPageAction(PAGE_ACTION.NEW_EDIT);
        setProgram({
            program_id: undefined,
            name: undefined,
            iso: undefined,
            utility_id: undefined,
            type_id: undefined,
            access_partner_id: undefined,
            description: undefined,
            utility_customer_id: undefined,
        });
    }

    const showPageActionModal = (action: PAGE_ACTION, record?: IProgram) => () => {
        setPageAction(action);
        setProgram(record || null);
    };

    function deleteProgramWithConfirm(record: IProgram) {
        if (isProgramDeleteLoading) return;

        modal.confirm({
            icon: <ExclamationCircleOutlined />,
            content: (
                <div>
                    <Typography.Text>
                        Deleting program <strong>{record.name || ''}</strong>.
                    </Typography.Text>
                    <br />
                    <Typography.Text>
                        This action will automatically unenroll all sites and unlink all products from this program. Are you certain you want to
                        proceed?
                    </Typography.Text>
                </div>
            ),
            onOk() {
                deleteProgram(record);
            },
            onCancel() {
                console.log('Cancel');
            },
        });
    }

    async function onTableChange(
        pagination: TablePaginationConfig,
        filter: Record<string, FilterValue | null>,
        sorter: SorterResult<IProgram> | SorterResult<IProgram>[]
    ) {
        const preparedParams = toUsefulParams({ pagination, sorter, filter });
        setPageQuery({ ...pageState, ...preparedParams });
    }

    async function onProgramFormModalClose(isRefreshNeeded: boolean = false) {
        setPageAction('');
        setProgram(null);

        if (isRefreshNeeded) {
            await refetchProgramList(listQuery);
        }
    }

    function downloadAttachedSites(record: IProgram) {
        return async () => {
            setSitesExportLoading(true);
            try {
                await exportSites({ filter: { program_id: record.program_id } });
            } catch (err: any) {
                notification.error({
                    key: 'sites-export-error',
                    message: err.message || 'Cannot export sites by program!',
                });
            }
            setSitesExportLoading(false);
        };
    }

    const onSearch = async (value: string) => {
        setPageQuery({ ...pageState, search: value });

        // if search value is the same as in the state, then we need to refetch data and set pagination to 1 page
        if (value === (pageState.search || '')) {
            listQuery.pagination.current = DEFAULT_PAGINATION.current;

            await refetchProgramList(listQuery);
        }
    };

    const actions = (programRecord: IProgram) => {
        const actionList = [
            ...(ability.can('update', 'Program')
                ? [
                      {
                          title: 'Edit',
                          onClick: (record: IProgram) => showPageActionModal(PAGE_ACTION.NEW_EDIT, record),
                      },
                  ]
                : []),
            {
                title: (record: IProgram) =>
                    record.enrolled_sites_count === 0 ? 'No sites enrolled to program to download' : 'Download sites attached to program',
                onClick: (record: IProgram) => (sitesExportLoading || record.enrolled_sites_count === 0 ? null : downloadAttachedSites(record)),
                icon: (record: IProgram) =>
                    sitesExportLoading ? (
                        <LoadingOutlined spin style={{ fontSize: 18 }} />
                    ) : (
                        <DownloadOutlined style={{ color: record.enrolled_sites_count === 0 ? '#ccc' : 'inherit' }} />
                    ),
            },
            ...(auth.user?.isAdminRoleType()
                ? [
                      {
                          title: 'Audit Trail',
                          onClick: (record: IProgram) => showPageActionModal(PAGE_ACTION.AUDIT_TRAIL, record),
                          icon: <HistoryOutlined />,
                      },
                  ]
                : []),

            ...(auth.user?.isAdmin()
                ? [
                      {
                          title: 'Delete Program',
                          onClick: (record: IProgram) => () => (record.program_locked ? null : deleteProgramWithConfirm(record)),
                          icon: (record: IProgram) => (
                              <LoadingIcon
                                  icon={<DeleteOutlined style={{ color: record.program_locked ? '#ccc' : 'inherit' }} />}
                                  loading={isProgramDeleteLoading}
                              />
                          ),
                      },
                  ]
                : []),
        ];

        return actionList;
    };

    const allowedActor = useMemo(() => !auth.user?.isCustomer(), [auth.user]);

    const isShowNominations = auth.user?.isAdminRoleType() || auth.user?.isCustomer();

    const columns: ColumnsType<IProgram> = [
        {
            title: 'Name',
            dataIndex: 'name',
            minWidth: 150,
            fixed: 'left',
            sorter: (a, b) => compareWithLocale(a.name, b.name),
            ...getColumnSortOrder('name', pageState.sorter),
            render: (programName, record) => (
                <div style={{ maxWidth: '180px' }}>
                    {record.program_locked && (
                        <Tooltip
                            title={
                                <div style={{ maxWidth: '110px' }}>
                                    This program is locked as it`s associated with DRMS automation. Please contact admin.
                                </div>
                            }
                        >
                            <LockOutlined style={{ marginRight: 4 }} />
                        </Tooltip>
                    )}
                    {programName}
                </div>
            ),
        },
        ...(auth.user?.isUtilityCustomer() || auth.user?.isControlProvider()
            ? [
                  {
                      title: 'Utility Customer',
                      dataIndex: 'utility_customer_id',
                      minWidth: 100,
                      render: (text: string, record: IProgram) => record?.utility_customer?.company_name,
                      ...(!auth.user?.isUtilityCustomer()
                          ? {
                                sorter: (a: IProgram, b: IProgram) =>
                                    compareWithLocale(a.utility_customer?.company_name, b.utility_customer?.company_name),
                                ...getColumnSortOrder('utility_customer_id', pageState.sorter),
                                filters: buildUtilityCustomerFilter(utilityCustomerCompanies),
                                ...getColumnFilteredValue('utility_customer_id', pageState.filter),
                                filterSearch: (input: string, record: ColumnFilterItem) =>
                                    !!record?.text?.toString()?.toLowerCase().includes(input.toLowerCase()),
                            }
                          : {}),
                  },
              ]
            : []),
        {
            title: 'Utility/ISO',
            dataIndex: 'utility_id',
            width: 120,
            render: (text, record) => {
                return <UtilityOrIsoTag utilityOrIso={record.iso || record.utility_name} />;
            },
        },
        {
            title: 'Operator',
            dataIndex: 'access_partner_id',
            width: 150,
            sorter: (a, b) => compareWithLocale(a.access_partner_id, b.access_partner_id),
            ...getColumnSortOrder('access_partner_id', pageState.sorter),
            filters: operators,
            ...getColumnFilteredValue('access_partner_id', pageState.filter),
            filterSearch: (input, record) => !!record?.text?.toString()?.toLowerCase().includes(input.toLowerCase()),
            render: (text, record) => record?.access_partner?.name,
        },
        {
            title: 'Type',
            dataIndex: 'type_id',
            width: 150,
            sorter: (a, b) => compareWithLocale(a.type_id, b.type_id),
            ...getColumnSortOrder('type_id', pageState.sorter),
            filters: types,
            ...getColumnFilteredValue('type_id', pageState.filter),
            filterSearch: (input, record) => !!record?.text?.toString()?.toLowerCase().includes(input.toLowerCase()),
            render: (text, record) => record?.type?.name,
        },
        {
            title: 'Sites Enrolled',
            dataIndex: 'enrolled_sites_count',
            minWidth: 100,
            sorter: (a: IProgram, b: IProgram) => a.enrolled_sites_count! - b.enrolled_sites_count!,
            ...getColumnSortOrder('enrolled_sites_count', pageState.sorter),
            render: (count, record) => count || 0,
        },
        ...(allowedActor
            ? [
                  {
                      title: 'Salesforce Products Linked',
                      dataIndex: 'products_count',
                      minWidth: 100,
                      sorter: (a: IProgram, b: IProgram) => Number(a.products?.length) - Number(b.products?.length)!,
                      ...getColumnSortOrder('products_count', pageState.sorter),
                      render: (count: void, record: IProgram) => record.products?.length || 0,
                  },
              ]
            : []),
        ...(auth.user?.isAdminRoleType()
            ? [
                  {
                      title: 'Utility Automation',
                      dataIndex: 'utility_automation',
                      minWidth: 100,
                      key: 'utility_automation',
                      ellipsis: true,

                      filters: utilityAutomationFilterOptions,
                      filterSearch: true,
                      filterMultiple: false,
                      ...getColumnFilteredValue('utility_automation', pageState.filter),

                      render: (text: string, record: IProgram) => <ProgramUtilityAutomations program={record} />,
                  },
              ]
            : []),
        ...(isShowNominations
            ? [
                  {
                      title: 'Previous Nominated kW',
                      dataIndex: 'previous_nomination',
                      minWidth: 100,
                      sorter: (a: IProgram, b: IProgram) => a.previous_nomination! - b.previous_nomination!,
                      ...getColumnSortOrder('previous_nomination', pageState.sorter),
                      render: (text: string, record: IProgram) =>
                          record.previous_nomination ? numberFormatter.format(record.previous_nomination) : '-',
                  },
                  {
                      title: 'Current Nominated kW',
                      dataIndex: 'current_nomination',
                      minWidth: 100,
                      sorter: (a: IProgram, b: IProgram) => a.current_nomination! - b.current_nomination!,
                      ...getColumnSortOrder('current_nomination', pageState.sorter),
                      render: (text: string, record: IProgram) =>
                          record.current_nomination ? numberFormatter.format(record.current_nomination) : '-',
                  },
                  {
                      title: 'Upcoming Nominated kW',
                      dataIndex: 'upcoming_nomination',
                      minWidth: 100,
                      sorter: (a: IProgram, b: IProgram) => a.upcoming_nomination! - b.upcoming_nomination!,
                      ...getColumnSortOrder('upcoming_nomination', pageState.sorter),
                      render: (text: string, record: IProgram) =>
                          record.upcoming_nomination ? numberFormatter.format(record.upcoming_nomination) : '-',
                  },
              ]
            : []),
        {
            key: 'action',
            minWidth: 60,
            fixed: 'right',
            sorter: false,
            render: (text, record) => <ActionList actions={actions(record)} item={record} />,
        },
    ];

    return (
        <>
            <PageHeader
                pageTitle={`Programs (${meta.total || 0})`}
                actions={[
                    <SearchInput onSearch={onSearch} defaultValue={pageState?.search} key="search-program" />,
                    auth.user?.isAdmin() && (
                        <Button
                            size="large"
                            key="upload-data"
                            data-cy="upload-program-to-product-relations"
                            onClick={showPageActionModal(PAGE_ACTION.UPLOAD_FILE)}
                            icon={<UploadOutlined />}
                        >
                            Upload CSV
                        </Button>
                    ),
                    <ProgramExportButton filter={listQuery} key="programs-export" />,
                    ability.can('create', 'AsyncJob') && <SyncSalesforceButton jobName={JobName.SYNC_SALESFORCE_PRODUCTS} key="salesforce-sync" />,
                    ability.can('create', 'Program') && (
                        <Button size="large" key="new-program" type="primary" onClick={showNewProgramModal} icon={<PlusCircleOutlined />}>
                            New Program
                        </Button>
                    ),
                ]}
            />
            <WithEmptyDataTable condition={isFetching}>
                <Table
                    size="small"
                    rowKey="program_id"
                    columns={columns}
                    dataSource={programs}
                    pagination={getTablePaginationParams(data?.meta)}
                    loading={isFetching}
                    onChange={onTableChange}
                    sticky={{ offsetHeader: 62 }}
                    tableLayout="auto"
                    scroll={{ x: 'max-content' }}
                />
            </WithEmptyDataTable>

            {program && pageAction === PAGE_ACTION.NEW_EDIT && <ProgramFormModal program={program} onClose={onProgramFormModalClose} />}

            {program && pageAction === PAGE_ACTION.AUDIT_TRAIL && (
                <AuditTrailModal entityId={program.program_id!} entityType={AuditEntityType.PROGRAM} onClose={onProgramFormModalClose} />
            )}

            {pageAction === PAGE_ACTION.UPLOAD_FILE && <UploadProgramToProductRelationModal onClose={onProgramFormModalClose} />}
        </>
    );
};
